import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2 } from 'lucide-react';
import { CheckCircle, XCircle, ArrowLeft, ArrowRight } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets-cm19k8pv.emergentagent.net/job_fastapi-react-crm-4/artifacts/slhdexmp_O-SCOP%20logo%20-%20copie%20-%20copie.png';

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState(token ? 'loading' : 'error');
  const [message, setMessage] = useState(token ? '' : 'Lien de confirmation invalide (token manquant).');
  const fired = useRef(false);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    axios.post(`${API}/api/auth/verify-email`, { token })
      .then((r) => { setState('ok'); setMessage(r.data?.message || 'Adresse email confirmée.'); })
      .catch((e) => {
        setState('error');
        const d = e.response?.data?.detail;
        setMessage(typeof d === 'string' ? d : 'Ce lien de confirmation est invalide ou expiré.');
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F5] p-6" data-testid="verify-email-page">
      <Card className="w-full max-w-md border-[#E8E6E1] shadow-sm">
        <CardContent className="pt-10 pb-8 text-center">
          <img src={LOGO_URL} alt="Objectif SCOP Outremer" className="w-14 h-14 object-contain mx-auto mb-5" />

          {state === 'loading' && (
            <div data-testid="verify-loading">
              <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C] mx-auto mb-4" />
              <h1 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Confirmation en cours…</h1>
              <p className="mt-2 text-sm text-[#5E6D66]">Nous activons votre espace, un instant.</p>
            </div>
          )}

          {state === 'ok' && (
            <div data-testid="verify-success">
              <div className="w-14 h-14 bg-[#80C51F]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={30} weight="fill" className="text-[#80C51F]" />
              </div>
              <h1 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Votre espace est activé !</h1>
              <p className="mt-2 text-sm text-[#5E6D66] leading-relaxed">{message}</p>
              <p className="mt-3 text-xs text-[#5E6D66]">Connectez-vous maintenant et complétez votre profil pour finaliser votre adhésion.</p>
              <Link to="/login" className="block mt-6">
                <Button className="w-full bg-[#2D5A4C] hover:bg-[#1E4136] text-white" data-testid="verify-goto-login">
                  Se connecter <ArrowRight className="ml-2" size={16} weight="bold" />
                </Button>
              </Link>
            </div>
          )}

          {state === 'error' && (
            <div data-testid="verify-error">
              <div className="w-14 h-14 bg-[#EA3C3A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle size={30} weight="fill" className="text-[#EA3C3A]" />
              </div>
              <h1 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Lien invalide ou expiré</h1>
              <p className="mt-2 text-sm text-[#5E6D66] leading-relaxed">{message}</p>
              <p className="mt-3 text-xs text-[#8A968F]">
                Vous pouvez demander un nouveau lien depuis la page de connexion, rubrique « Renvoyer l'email de confirmation ».
              </p>
              <Link to="/login" className="block mt-6">
                <Button variant="outline" className="w-full border-[#E8E6E1]" data-testid="verify-back-login">
                  <ArrowLeft className="mr-2" size={16} weight="bold" /> Retour à la connexion
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
